import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Empty, Image, message as antdMessage, Modal, Spin, Typography } from 'antd';
import { CloseOutlined, DownloadOutlined, EllipsisOutlined, GlobalOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import ReactPlayer from 'react-player';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';
import PDFLessonViewer from '../../pages/LessonPage2.0/LessonComponents/PDFLessonViewer';
import { getProfilePictureUrl } from '../profile/profilePictureUrl';
import './Noticeboard.scss';
import './NoticeBoard.css';
const { Text } = Typography;
const AVATAR_PALETTE = ['#1877F2', '#42B883', '#F59E0B', '#EF4444', '#8B5CF6', '#0EA5E9', '#14B8A6', '#EC4899'];
const MESSAGE_LIMIT = 220;

const getInitials = (value) =>
   `${value || 'N'}`
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase() || 'N';

const hashCode = (value) =>
   `${value || ''}`
      .split('')
      .reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0, 0);

const buildAvatarColor = (value) => AVATAR_PALETTE[hashCode(value) % AVATAR_PALETTE.length];

const normalizeFiles = (filelist) => {
   if (!filelist) return [];

   return `${filelist}`
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
};

const resolveAttachmentUrl = (itemId, fileName, subdomain) => {
   if (!fileName) return '';
   if (/^https?:\/\//i.test(fileName)) return fileName;

   return `${process.env.REACT_APP_API_S3_URL}/${subdomain}/announcementfiles/${itemId}/${fileName}`;
};

const getFileType = (fileName) => {
   const extension = `${fileName || ''}`.split('.').pop().toLowerCase();

   if (['png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp'].includes(extension)) return 'image';
   if (['mp4', 'webm', 'mov'].includes(extension)) return 'video';
   if (extension === 'pdf') return 'pdf';
   return 'file';
};

// Builds an API url that streams the stored file with a Content-Disposition: attachment
// header, so the browser always downloads it instead of opening it in a new tab.
const buildDownloadApiUrl = (fileUrl, fileName) => {
   const baseUrl = process.env.REACT_APP_API_S3_URL;
   if (!fileUrl || !baseUrl) return '';

   const prefix = `${baseUrl}/`;
   if (!fileUrl.startsWith(prefix)) return '';

   const objectKey = fileUrl.slice(prefix.length).split('?')[0].split('#')[0];
   if (!objectKey) return '';

   return `${process.env.REACT_APP_LMS_API}api/AWS/download?key=${encodeURIComponent(objectKey)}&fileName=${encodeURIComponent(fileName)}`;
};

const saveBlobAsFile = (blob, fileName) => {
   const objectUrl = window.URL.createObjectURL(blob);
   const link = document.createElement('a');
   link.href = objectUrl;
   link.download = fileName || 'attachment';
   link.rel = 'noopener';
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};

const formatPostedAt = (value) => {
   if (!value) return 'Just now';

   const parsed = moment(value);
   if (!parsed.isValid()) return `${value}`;

   return parsed.format('MMM D, YYYY h:mm A');
};

const getDisplayName = (item) =>
   item.fullname ||
   item.posted_by_name ||
   item.user_name ||
   item.username ||
   item.created_by_name ||
   item.author_name ||
   item.staff_name ||
   item.title ||
   'Notice Board';

const getAuthorId = (item) =>
   item.posted_by_id ||
   item.postedById ||
   item.created_by_id ||
   item.createdById ||
   item.user_id ||
   item.userId ||
   item.staffid ||
   item.staff_id ||
   item.created_by ||
   item.posted_by ||
   null;

const getProfileName = (profile) => {
   if (!profile) return '';

   const name = [profile.first_name, profile.middle_name, profile.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();

   return name || profile.fullname || profile.full_name || profile.name || profile.username || '';
};

const getAvatarUrl = (item, subdomain) => {
   const candidate = item.avatar || item.picture || item.profile_picture || item.profilePicture || item.user_picture;

   if (!candidate) return '';
   if (/^https?:\/\//i.test(candidate)) return candidate;

   return `${process.env.REACT_APP_API_S3_URL}/${subdomain}/${candidate}`;
};

const getMessagePreview = (message, expanded) => {
   if (!message) return '';
   if (expanded || message.length <= MESSAGE_LIMIT) return message;
   return `${message.slice(0, MESSAGE_LIMIT).trimEnd()}...`;
};

function NoticeBoard(props) {
   const [dataContent, setDataContent] = useState([]);
   const [authorProfiles, setAuthorProfiles] = useState({});
   const [loading, setLoading] = useState(true);
   const [visible, setVisible] = useState(false);
   const [message, setMessage] = useState('');
   const [postedDate, setPostedDate] = useState('');
   const [currentAttachments, setCurrentAttachments] = useState([]);
   const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);
   const [currentTitle, setCurrentTitle] = useState('');
   const [downloadStatus, setDownloadStatus] = useState(false);
   const [expandedMessages, setExpandedMessages] = useState({});
   const subdomain = localStorage.getItem('lms-subdomain');

   const feedItems = useMemo(
      () =>
         dataContent.map((item) => {
            const authorProfile = authorProfiles[`${getAuthorId(item)}`];
            const attachments = normalizeFiles(item.filelist).map((fileName) => ({
               name: fileName,
               url: resolveAttachmentUrl(item.id, fileName, subdomain),
               type: getFileType(fileName),
            }));

            return {
               ...item,
               attachments,
               displayName: getProfileName(authorProfile) || getDisplayName(item),
               avatarUrl: authorProfile?.noticeAuthorId
                  ? getProfilePictureUrl(
                     `userprofiles/${authorProfile.noticeAuthorId}/${authorProfile.noticeAuthorId}.png`,
                     subdomain,
                     Date.now()
                  )
                  : getAvatarUrl(item, subdomain),
               postedAt: formatPostedAt(item.start_date || item.posted_date || item.created_at),
               message: item.content || item.message || item.body || '',
            };
         }),
      [authorProfiles, dataContent, subdomain]
   );

   useEffect(() => {
      Utils.verifyJwt();
      getNoticeList();
   }, [props.uid]);

   useEffect(() => {
      let isCurrent = true;
      const authorIds = [...new Set(dataContent.map(getAuthorId).filter(Boolean))];

      if (!authorIds.length) {
         setAuthorProfiles({});
         return undefined;
      }

      const getAuthorProfile = async (authorId) => {
         try {
            const staffResponse = await axios.get(`${process.env.REACT_APP_API_MANAGEMENT}/getstaffinfo?id=${authorId}`);
            if (staffResponse.data) return staffResponse.data;
         } catch {
            // A notice can also be posted by a student account.
         }

         try {
            const studentResponse = await axios.get(`${process.env.REACT_APP_API_MANAGEMENT}/getstudentinfo?id=${authorId}`);
            return studentResponse.data || null;
         } catch {
            return null;
         }
      };

      Promise.all(authorIds.map(async (authorId) => {
         const profile = await getAuthorProfile(authorId);
         return [authorId, profile ? { ...profile, noticeAuthorId: authorId } : null];
      }))
         .then((profiles) => {
            if (!isCurrent) return;

            setAuthorProfiles(profiles.reduce((result, [authorId, profile]) => {
               if (profile) result[`${authorId}`] = profile;
               return result;
            }, {}));
         });

      return () => {
         isCurrent = false;
      };
   }, [dataContent]);

   const toggleMessage = (itemId) => {
      setExpandedMessages((current) => ({
         ...current,
         [itemId]: !current[itemId],
      }));
   };

   const openViewer = (item, attachmentIndex = 0) => {
      setCurrentTitle(item.displayName);
      setPostedDate(item.postedAt);
      setMessage(item.message);
      setCurrentAttachments(item.attachments);
      setCurrentAttachmentIndex(Math.max(0, Math.min(attachmentIndex, item.attachments.length - 1)));
      setVisible(true);
   };

   const closeViewer = () => {
      setVisible(false);
   };

   const goToPreviousAttachment = () => {
      setCurrentAttachmentIndex((current) =>
         current <= 0 ? currentAttachments.length - 1 : current - 1
      );
   };

   const goToNextAttachment = () => {
      setCurrentAttachmentIndex((current) =>
         current >= currentAttachments.length - 1 ? 0 : current + 1
      );
   };

   const downloadAttachment = async (attachment) => {
      if (!attachment?.url) return;

      setDownloadStatus(true);
      const fileName = `${attachment.name || 'attachment'}`.split('/').pop().split('?')[0] || 'attachment';

      try {
         let blob = null;
         const downloadApiUrl = buildDownloadApiUrl(attachment.url, fileName);

         // Preferred: stream the file through the API so the server sets
         // Content-Disposition: attachment. This works for every storage host and
         // guarantees a download instead of the browser previewing the file.
         if (downloadApiUrl) {
            try {
               const response = await axios.get(downloadApiUrl, { responseType: 'blob' });
               blob = response.data;
            } catch (apiError) {
               console.warn('API download failed, falling back to direct storage download.', apiError);
            }
         }

         // Fallback: download the file straight from storage (requires the storage
         // host to allow cross-origin reads).
         if (!blob) {
            const response = await fetch(attachment.url);
            if (!response.ok) throw new Error(`Failed to download file (${response.status})`);
            blob = await response.blob();
         }

         saveBlobAsFile(blob, fileName);
      } catch (error) {
         console.log(error?.message || error);
         antdMessage.error('Unable to download the attachment. Please try again.');
      } finally {
         setDownloadStatus(false);
      }
   };

   useEffect(() => {
      if (!visible) return undefined;

      const handleKeyDown = (event) => {
         if (event.key === 'Escape') {
            closeViewer();
            return;
         }

         if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goToPreviousAttachment();
            return;
         }

         if (event.key === 'ArrowRight') {
            event.preventDefault();
            goToNextAttachment();
         }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => window.removeEventListener('keydown', handleKeyDown);
   }, [visible, currentAttachments.length, closeViewer, goToPreviousAttachment, goToNextAttachment]);

   const getNoticeList = () => {
      const user = Utils.getCurrentUserInfo(Enums.UserInfo.id);
      const role = Utils.getCurrentUserInfo(Enums.UserInfo.role);
      const url = `${process.env.REACT_APP_LMS_API}api/NoticeBoard/listEntityNoticeList?userid=${user}&role=${role}`;

      setLoading(true);
      axios.get(url).then(
         (response) => {
            setDataContent(Array.isArray(response.data) ? response.data : []);
            setLoading(false);
         },
         (error) => {
            console.log(error.message);
            setDataContent([]);
            setLoading(false);
         }
      );
   };

   const renderAttachment = (attachment) => {
      if (attachment.type === 'image') {
         return (
            <div className="noticeboard-media-image-frame">
               <Image
                  src={attachment.url}
                  alt={attachment.name}
                  preview={false}
                  className="noticeboard-media-image"
               />
            </div>
         );
      }

      if (attachment.type === 'video') {
         return (
            <div className="noticeboard-media-placeholder noticeboard-media-placeholder-video">
               <ReactPlayer
                  className="noticeboard-media-player"
                  controls
                  url={attachment.url}
                  width="100%"
                  height="100%"
                />
            </div>
         );
      }

      if (attachment.type === 'pdf') {
         return (
            <div className="noticeboard-media-placeholder noticeboard-media-placeholder-pdf">
               <PDFLessonViewer fileUrl={attachment.url} />
            </div>
         );
      }

      return (
         <div className="noticeboard-media-placeholder noticeboard-media-placeholder-file">
            <span>{attachment.name}</span>
         </div>
      );
   };

   const renderMediaGallery = (item) => {
      if (!item.attachments.length) return null;

      const previewAttachments = item.attachments.length > 1 ? item.attachments.slice(0, 3) : item.attachments;
      const extraCount = item.attachments.length - previewAttachments.length;
      const galleryClassName =
         previewAttachments.length === 1
            ? 'noticeboard-media-gallery noticeboard-media-gallery-single'
            : 'noticeboard-media-gallery noticeboard-media-gallery-collage';

      return (
         <div className={galleryClassName}>
            {previewAttachments.map((attachment, index) => (
               <div
                  key={`${item.id}-${attachment.name}-${index}`}
                  className={[
                     'noticeboard-media-tile',
                     `noticeboard-media-tile-${Math.min(previewAttachments.length, 5)}`,
                     `noticeboard-media-tile-index-${index}`,
                  ].join(' ')}
                  onClick={() => openViewer(item, index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                     if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openViewer(item, index);
                     }
                  }}
               >
                  {renderAttachment(attachment)}
                  {index === previewAttachments.length - 1 && extraCount > 0 && (
                     <div
                        className="noticeboard-media-overlay"
                        onClick={(event) => {
                           event.stopPropagation();
                           openViewer(item, index);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                           if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openViewer(item, index);
                           }
                        }}
                     >
                        +{extraCount}
                     </div>
                  )}
               </div>
            ))}
         </div>
      );
   };

   const renderFeedCard = (item) => {
      const hasAttachments = item.attachments.length > 0;
      const isExpanded = Boolean(expandedMessages[item.id]);
      const shouldTrimMessage = item.message && item.message.length > MESSAGE_LIMIT;
      const previewMessage = getMessagePreview(item.message, isExpanded);

      return (
         <article className="noticeboard-feed-card">
            <div className="noticeboard-feed-header">
               <div className="noticeboard-feed-author">
                  <Avatar
                     size={52}
                     src={item.avatarUrl || undefined}
                     className="noticeboard-feed-avatar"
                     style={!item.avatarUrl ? { backgroundColor: buildAvatarColor(item.displayName) } : undefined}
                     icon={!item.avatarUrl ? <UserOutlined /> : undefined}
                  >
                     {!item.avatarUrl ? getInitials(item.displayName) : null}
                  </Avatar>
                  <div className="noticeboard-feed-author-copy">
                     <div className="noticeboard-feed-meta">
                        <span>{item.postedAt}</span>
                        <GlobalOutlined />
                     </div>
                     <div className="noticeboard-feed-name-row">
                        <h3>{item.displayName}</h3>
                     </div>
                  </div>
               </div>

               {/* <div className="noticeboard-feed-actions" aria-hidden="true">
                  <EllipsisOutlined />
                  <CloseOutlined />
               </div> */}
            </div>

            {item.message && (
               <div className="noticeboard-feed-message-wrap">
                  <div className={[
                     'noticeboard-feed-message',
                     shouldTrimMessage && !isExpanded ? 'noticeboard-feed-message-clamped' : '',
                  ].filter(Boolean).join(' ')}>
                     {previewMessage}
                  </div>
                  {shouldTrimMessage && (
                     <button
                        type="button"
                        className="noticeboard-message-toggle"
                        onClick={() => toggleMessage(item.id)}
                     >
                        {isExpanded ? 'See Less' : 'See More'}
                     </button>
                  )}
               </div>
            )}

            {hasAttachments && (
               <>
                  {renderMediaGallery(item)}
                  {item.attachments.length > 5 && (
                     <button className="noticeboard-view-all" type="button" onClick={() => openViewer(item)}>
                        View all photos
                     </button>
                  )}
               </>
            )}
         </article>
      );
   };

   return (
   <div className="dashboard-area-all home-dashboard-page">
      <div className="dashboard-item home-dashboard">
         <div className="home-dashboard-breadcrumb">
            <span className="home-dashboard-breadcrumb-label"><a href="/home">Home</a> / School Feed</span>
         </div>      
         <section className="home-dashboard-hero">
               <div className="home-dashboard-hero-copy">
                  <Text className="home-dashboard-kicker">Brainee LMS</Text>
                  <h1>School Feed</h1>
                  <p>
                     School announcements, notices, and media posts.
                  </p>
                 </div>    
            </section>         
      <div className="noticeboard-feed-page">
         <div className="noticeboard-feed-shell">
            {/* <div className="noticeboard-feed-hero">
               <span className="noticeboard-kicker">Announcement</span>
               <h1>Notice Board</h1>
               <p>School announcements, notices, and media posts.</p>
            </div> */}

            {loading ? (
               <div className="noticeboard-feed-loading">
                  <Spin size="large" />
                  <div className="noticeboard-feed-loading-text">Loading announcements...</div>
               </div>
            ) : feedItems.length > 0 ? (
               <div className="noticeboard-feed-list">
                  {feedItems.map((item) => (
                     <React.Fragment key={item.id || `${item.displayName}-${item.postedAt}`}>
                        {renderFeedCard(item)}
                     </React.Fragment>
                  ))}
               </div>
            ) : (
               <div className="noticeboard-feed-empty">
                  <Empty description="No Announcements" image={Empty.PRESENTED_IMAGE_SIMPLE} />
               </div>
            )}
            </div>
         </div>
      </div>


         <Modal
            title={`${currentTitle || 'Notice Board'} - ${postedDate}`}
            centered
            open={visible}
            onOk={closeViewer}
            onCancel={closeViewer}
            width={980}
            className="noticeboard-feed-modal"
            footer={null}
         >
            <div className="noticeboard-modal-body">
               {message && <div className="noticeboard-modal-message">{message}</div>}
               {currentAttachments.length > 0 && (
                  <div className="noticeboard-viewer">
                     <button
                        type="button"
                        className="noticeboard-viewer-nav noticeboard-viewer-nav-left"
                        onClick={goToPreviousAttachment}
                     >
                        &#8249;
                     </button>

                     <div className="noticeboard-viewer-stage">
                        <div className="noticeboard-viewer-counter">
                           {currentAttachmentIndex + 1} / {currentAttachments.length}
                        </div>
                        <button
                           type="button"
                           className="noticeboard-viewer-download"
                           onClick={() => downloadAttachment(currentAttachments[currentAttachmentIndex])}
                           disabled={downloadStatus}
                           aria-label="Download attachment"
                           title="Download attachment"
                        >
                           {downloadStatus ? <Spin size="small" /> : <DownloadOutlined />}
                           <span className="noticeboard-viewer-download-label">Download</span>
                        </button>
                        {renderAttachment(currentAttachments[currentAttachmentIndex])}
                     </div>

                     <button
                        type="button"
                        className="noticeboard-viewer-nav noticeboard-viewer-nav-right"
                        onClick={goToNextAttachment}
                     >
                        &#8250;
                     </button>
                  </div>
               )}
            </div>
         </Modal>
      </div>
   );
}

export default NoticeBoard;
